import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const OfficeLocationsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [selectedLocation, setSelectedLocation] = useState('mumbai');

  const translations = {
    en: {
      title: "Our Office Locations",
      subtitle: "Connect with our regional teams across major manufacturing hubs in India",
      viewDetails: "View Details",
      getDirections: "Get Directions",
      callOffice: "Call Office"
    },
    hi: {
      title: "हमारे कार्यालय स्थान",
      subtitle: "भारत के प्रमुख निर्माण केंद्रों में हमारी क्षेत्रीय टीमों से जुड़ें",
      viewDetails: "विवरण देखें",
      getDirections: "दिशा निर्देश प्राप्त करें",
      callOffice: "कार्यालय में कॉल करें"
    }
  };

  const t = translations[currentLanguage];

  const officeLocations = {
    mumbai: {
      name: currentLanguage === 'en' ? 'Mumbai (Head Office)' : 'मुंबई (मुख्य कार्यालय)',
      address: currentLanguage === 'en' ?'A-401, Business Park, Andheri East, Mumbai - 400069' :'A-401, बिज़नेस पार्क, अंधेरी पूर्व, मुंबई - 400069',
      phone: '+91 22 6789 1234',
      email: 'mumbai@alumerp.com',
      hours: currentLanguage === 'en' ? 'Mon-Fri: 9:00 AM - 6:00 PM' : 'सोम-शुक्र: 9:00 AM - 6:00 PM',
      specialties: currentLanguage === 'en' 
        ? ['Implementation', 'Training', 'Support', 'Consulting']
        : ['कार्यान्वयन', 'प्रशिक्षण', 'सहायता', 'परामर्श'],
      teamSize: '25+',
      languages: currentLanguage === 'en' ? ['English', 'Hindi', 'Marathi'] : ['अंग्रेजी', 'हिंदी', 'मराठी']
    },
    delhi: {
      name: currentLanguage === 'en' ? 'Delhi NCR' : 'दिल्ली NCR',
      address: currentLanguage === 'en' ?'B-205, Sector 63, Noida, Uttar Pradesh - 201301' :'B-205, सेक्टर 63, नोएडा, उत्तर प्रदेश - 201301',
      phone: '+91 11 9876 5432',
      email: 'delhi@alumerp.com',
      hours: currentLanguage === 'en' ? 'Mon-Fri: 9:00 AM - 6:00 PM' : 'सोम-शुक्र: 9:00 AM - 6:00 PM',
      specialties: currentLanguage === 'en' 
        ? ['Implementation', 'Support', 'Training']
        : ['कार्यान्वयन', 'सहायता', 'प्रशिक्षण'],
      teamSize: '18+',
      languages: currentLanguage === 'en' ? ['English', 'Hindi', 'Punjabi'] : ['अंग्रेजी', 'हिंदी', 'पंजाबी']
    },
    bangalore: {
      name: currentLanguage === 'en' ? 'Bangalore' : 'बैंगलोर',
      address: currentLanguage === 'en' ?'C-301, Tech Park, Electronic City, Bangalore - 560100' :'C-301, टेक पार्क, इलेक्ट्रॉनिक सिटी, बैंगलोर - 560100',
      phone: '+91 80 8765 4321',
      email: 'bangalore@alumerp.com',
      hours: currentLanguage === 'en' ? 'Mon-Fri: 9:00 AM - 6:00 PM' : 'सोम-शुक्र: 9:00 AM - 6:00 PM',
      specialties: currentLanguage === 'en' 
        ? ['Technical Support', 'Development', 'Training']
        : ['तकनीकी सहायता', 'विकास', 'प्रशिक्षण'],
      teamSize: '22+',
      languages: currentLanguage === 'en' ? ['English', 'Hindi', 'Kannada'] : ['अंग्रेजी', 'हिंदी', 'कन्नड़']
    },
    chennai: {
      name: currentLanguage === 'en' ? 'Chennai' : 'चेन्नई',
      address: currentLanguage === 'en' ?'D-102, Industrial Estate, Ambattur, Chennai - 600058' :'D-102, इंडस्ट्रियल एस्टेट, अंबत्तूर, चेन्नई - 600058',
      phone: '+91 44 7654 3210',
      email: 'chennai@alumerp.com',
      hours: currentLanguage === 'en' ? 'Mon-Fri: 9:00 AM - 6:00 PM' : 'सोम-शुक्र: 9:00 AM - 6:00 PM',
      specialties: currentLanguage === 'en' 
        ? ['Implementation', 'Support', 'Consulting']
        : ['कार्यान्वयन', 'सहायता', 'परामर्श'],
      teamSize: '16+',
      languages: currentLanguage === 'en' ? ['English', 'Hindi', 'Tamil'] : ['अंग्रेजी', 'हिंदी', 'तमिल']
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleCallOffice = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleGetDirections = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://maps.google.com?q=${encodedAddress}`, '_blank');
  };

  return (
    <section id="office-locations" className="py-16 bg-surface-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Location Selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {Object.keys(officeLocations).map((locationKey) => (
            <button
              key={locationKey}
              onClick={() => setSelectedLocation(locationKey)}
              className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
                selectedLocation === locationKey
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-text-secondary hover:bg-surface-100 border border-border'
              }`}
            >
              {officeLocations[locationKey].name}
            </button>
          ))}
        </div>

        {/* Selected Location Details */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-border">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Location Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-2">
                  {officeLocations[selectedLocation].name}
                </h3>
                <div className="flex items-start space-x-3 mb-4">
                  <Icon name="MapPin" size={20} color="var(--color-primary)" />
                  <p className="text-text-secondary">{officeLocations[selectedLocation].address}</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={18} color="var(--color-primary)" />
                  <span className="text-text-secondary">{officeLocations[selectedLocation].phone}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={18} color="var(--color-primary)" />
                  <span className="text-text-secondary">{officeLocations[selectedLocation].email}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={18} color="var(--color-primary)" />
                  <span className="text-text-secondary">{officeLocations[selectedLocation].hours}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleCallOffice(officeLocations[selectedLocation].phone)}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  <Icon name="Phone" size={16} />
                  <span>{t.callOffice}</span>
                </button>
                <button
                  onClick={() => handleGetDirections(officeLocations[selectedLocation].address)}
                  className="flex items-center space-x-2 px-4 py-2 bg-surface-100 text-text-secondary rounded-lg hover:bg-surface-200 transition-colors"
                >
                  <Icon name="Navigation" size={16} />
                  <span>{t.getDirections}</span>
                </button>
              </div>
            </div>

            {/* Office Details */}
            <div className="space-y-6">
              {/* Team Size */}
              <div className="bg-surface-50 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <Icon name="Users" size={20} color="var(--color-primary)" />
                  <h4 className="font-semibold text-primary">
                    {currentLanguage === 'en' ? 'Team Size' : 'टीम का आकार'}
                  </h4>
                </div>
                <p className="text-2xl font-bold text-primary">{officeLocations[selectedLocation].teamSize}</p>
                <p className="text-sm text-text-secondary">
                  {currentLanguage === 'en' ? 'ERP specialists' : 'ERP विशेषज्ञ'}
                </p>
              </div>

              {/* Specialties */}
              <div className="bg-surface-50 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <Icon name="Award" size={20} color="var(--color-primary)" />
                  <h4 className="font-semibold text-primary">
                    {currentLanguage === 'en' ? 'Specialties' : 'विशेषताएं'}
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {officeLocations[selectedLocation].specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 text-primary rounded-full text-sm"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div className="bg-surface-50 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <Icon name="Globe" size={20} color="var(--color-primary)" />
                  <h4 className="font-semibold text-primary">
                    {currentLanguage === 'en' ? 'Languages Supported' : 'समर्थित भाषाएं'}
                  </h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {officeLocations[selectedLocation].languages.map((language, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-accent-100 text-accent rounded-full text-sm"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfficeLocationsSection;